import {ActionFunction} from '../interface/action/action-function.type';
import {ActionEnum} from '../enum/action.enum';
import {IRoutePayload} from '../interface/action/route-payload.interface';
import {getPageBySlug} from '../util/action/type/get-page-by-slug-action.util';
import {PageModel} from '../model/page.model';
import {action, toJS} from 'mobx';
import {httpSavePage} from '../util/action/type/http-save-page-action.util';
import {httpUpdatePage} from '../util/action/type/http-update-page-action.util';
import {saveDesignObjectDraft} from '../util/action/type/save-design-object-draft-action.util';
import {deleteDesignObjectDraft} from '../util/action/type/delete-design-object-draft-action.util';
import {noop} from '../util/action/type/noop-action.util';
import {loadNewDesignObjectDraft} from '../util/action/type/load-new-page-draft-action.util';
import {DesignTypeEnum} from '../enum/design-type.enum';
import {convertPathToRegEx} from '../util/route/convert-path-to-regex';
import {getDesignDraftId} from '../util/page/get-design-draft-id.util';
import {isStringEmpty} from '../util/string/is-string-empty.util';
import {isPageModel} from '../util/design-object/is-page-model.util';
import {DesignObjectModel} from '../model/design-object.model';
import {getUniqueId} from '../util/string/get-unique-id.util';
import {IActionsOnChangePayload} from '../interface/block/edit-config.interface';
import {getDesignObjectMetadata} from '../util/action/type/get-design-object-metadata-action.util';
import {getHomePage} from '../util/action/type/get-home-page-action.util';
import {redirectPage} from '../util/action/type/redirect-page.util';

export interface ILoadDynamicPagePayload {
  type: DesignTypeEnum;
  ignoreSlug: boolean;
  useCache: boolean;
}

export const loadDynamicPage$: ActionFunction<IRoutePayload & ILoadDynamicPagePayload> = (action$, {state, history}) => {
  return action$
    .type(ActionEnum.LOAD_DYNAMIC_PAGE)
    .map(action((payload: IRoutePayload & ILoadDynamicPagePayload) => {
      // JobAd preview URLs are sufixed with '/preview' and we have to remove that part
      let pathName = payload.location.pathname;
      let pathMatch = payload.match.path.replace('*', '');
      if (payload.type === DesignTypeEnum.JobAdPage && pathName.endsWith('/preview')) {
        pathName = pathName.substring(0, pathName.lastIndexOf('/preview'));
        pathMatch = pathMatch.substring(0, pathMatch.lastIndexOf('/preview'));
      }
      const pathRegEx = convertPathToRegEx(pathMatch);
      let slug = pathName.replace(pathRegEx, '').split('/')[ 0 ];
      let slugSEO = pathName.replace(pathRegEx, '').split('/')[ 1 ];
      if (payload.ignoreSlug) {
        slugSEO = slug;
        slug = '';
      }
      if (isStringEmpty(slug)) {
        slug = '/';
      }
      const oldLoadParamsKeys = Object.keys(state.activeDesign.loadParams.params || {});
      const loadParamsKeys = Object.keys(payload.match.params || {});
      if (state.activeDesign.data && isPageModel(state.activeDesign.data) && !state.activeDesign.data.isNew && payload.type === state.activeDesign.loadParams.type && slug === state.activeDesign.loadParams.slug && oldLoadParamsKeys.length === loadParamsKeys.length && loadParamsKeys.every((key, index) => key === oldLoadParamsKeys[ index ] && payload.match.params[ key ] === state.activeDesign.loadParams.params[ key ])) {
        return noop();
      }
      state.activeDesign.loadParams.slug = slug;
      state.activeDesign.loadParams.slugSEO = slugSEO;
      state.activeDesign.loadParams.type = payload.type;
      state.activeDesign.loadParams.params = payload.match.params;
      if (state.activeDesign.data && isPageModel(state.activeDesign.data)) {
        state.pagesScrollLocation[ state.activeDesign.data.id ] = window.scrollY || window.pageYOffset || window.document.documentElement.scrollTop;
      }
      if ((state.activeDesign.loadParams.slug === '/' || state.activeDesign.loadParams.slug.indexOf('index') === 0) && !state.configuration.inAdmin) {
        return getHomePage(state.activeDesign.loadParams.type, state.activeDesign.loadParams.filterParameters, payload.useCache);
      }
      return getPageBySlug(state.activeDesign.loadParams.slug, payload.useCache, state.activeDesign.loadParams.type, state.activeDesign.loadParams.filterParameters);
    }));
};

export const savePage$: ActionFunction<PageModel> = (action$) => {
  return action$
    .type(ActionEnum.SAVE_PAGE)
    .map(action((page: PageModel) => {
      page.isNew = false;
      return [ httpSavePage(page), deleteDesignObjectDraft(page, false) ];
    }));
};

export const updatePage$: ActionFunction<PageModel> = (action$, {state}) => {
  return action$
    .type(ActionEnum.UPDATE_PAGE)
    .map((page) => {
      return [ httpUpdatePage(state.activeDesign.data.id, page), deleteDesignObjectDraft(page, false) ];
    });
};

export const newDynamicPage$: ActionFunction = (action$, {state, logger}) => {
  return action$
    .type(ActionEnum.NEW_DYNAMIC_PAGE)
    .map(action(() => {
      if (isPageModel(state.activeDesign.data)) {
        if (state.activeDesign.data && state.activeDesign.data.isNew) {
          return noop();
        }
      }
      let pageDraft;
      try {
        pageDraft = JSON.parse(localStorage.getItem(getDesignDraftId()));
      } catch (e) {
        logger.warn('Can\'t load page from draft');
      }
      if (pageDraft && pageDraft.slug && pageDraft.slug !== '') {
        return loadNewDesignObjectDraft();
      }
      state.activeDesign.setDesignData({
        id: getUniqueId(),
        type: DesignTypeEnum.Page,
        backgroundColor: 'Transparent',
        dateUpdated: null,
        blocks: []
      }, state.configuration.inAdmin);
      if (isPageModel(state.activeDesign.data)) {
        return saveDesignObjectDraft(state.activeDesign.data);
      }
      return noop();
    }));
};

export const copyPage$: ActionFunction<PageModel> = (action$, {state}) => {
  return action$
    .type(ActionEnum.COPY_PAGE)
    .map(action((page: PageModel) => {
      const pageData = toJS(page);
      state.activeDesign.previousData = {...pageData} as any;
      delete pageData.id;
      delete pageData.isDraft;
      pageData.isNew = true;
      pageData.slug = '';
      pageData.title = undefined;
      state.activeDesign.setDesignData(pageData, state.configuration.inAdmin, true);
      if (isPageModel(state.activeDesign.data)) {
        state.activeDesign.data.isCopy = true;
        return [ redirectPage('/admin/pages/new-page'), saveDesignObjectDraft(state.activeDesign.data) ];
      }
      return noop();
    }));
};

export const getDesignObjectMetadataOnChange$: ActionFunction<IActionsOnChangePayload> = (action$) => {
  return action$
    .type(ActionEnum.GET_DESIGN_OBJECT_METADATA_ON_CHANGE)
    .map((payload: IActionsOnChangePayload) => {
      return getDesignObjectMetadata(payload.data.type, payload.data.customListDefinitionId);
    });
};

export interface IDeletePageDraftPayload {
  design: DesignObjectModel | string;
  shouldRevert?: boolean;
}

export default {
  actions: [
    loadDynamicPage$,
    savePage$,
    updatePage$,
    newDynamicPage$,
    copyPage$,
    getDesignObjectMetadataOnChange$
  ]
};
